import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import DocumentScanner from "../components/DocumentScanner";
import {
  Banknote,
  Plus,
  ArrowDownCircle,
  ArrowUpCircle,
  Receipt,
  MoreVertical,
  Pencil,
  Trash2,
  Download,
  Eye,
  Upload,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Wallet,
  Printer,
  ScanLine,
  ExternalLink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kategoriler
const INCOME_CATEGORIES = [
  "Barmiete",
  "Kaution",
  "Nebenkosten",
  "Sonstige Einnahmen",
];

const EXPENSE_CATEGORIES = [
  "Einkauf",
  "Reparatur",
  "Reinigung",
  "Bürobedarf",
  "Nebenkosten",
  "Sonstige Ausgaben",
];

export default function KassePage() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    current_balance: 0,
    transaction_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showScannerDialog, setShowScannerDialog] = useState(false);
  const [scannerTransactionId, setScannerTransactionId] = useState(null);
  const [scannerDescription, setScannerDescription] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Report state
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  // Form state
  const [formData, setFormData] = useState({
    transaction_type: "income",
    amount: "",
    description: "",
    date: format(new Date(), "yyyy-MM-dd"),
    category: "",
    reference: "",
    guest_id: "",
    receipt_data: null,
  });

  // Filter state
  const [filterType, setFilterType] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: format(new Date(new Date().setDate(1)), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kasse/summary`, {
        headers: getAuthHeaders(),
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching cash summary:", error);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      let url = `${API_URL}/api/kasse/transactions`;
      const params = new URLSearchParams();
      
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);
      if (filterType !== "all") params.append("transaction_type", filterType);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Fehler beim Laden der Kassenbuchungen");
    }
  }, [dateRange, filterType]);

  const fetchGuests = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guests`, {
        headers: getAuthHeaders(),
      });
      // Sadece arşivlenmemiş bewohner
      setGuests(response.data.filter((g) => !g.archived));
    } catch (error) {
      console.error("Error fetching guests:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchTransactions(), fetchGuests()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchTransactions, fetchGuests]);

  const resetForm = () => {
    setFormData({
      transaction_type: "income",
      amount: "",
      description: "",
      date: format(new Date(), "yyyy-MM-dd"),
      category: "",
      reference: "",
      guest_id: "",
      receipt_data: null,
    });
  };

  const handleAddTransaction = async () => {
    try {
      if (!formData.amount || !formData.description || !formData.date) {
        toast.error("Bitte füllen Sie alle Pflichtfelder aus");
        return;
      }

      await axios.post(
        `${API_URL}/api/kasse/transactions`,
        {
          ...formData,
          amount: parseFloat(formData.amount),
          guest_id: formData.guest_id || null,
        },
        { headers: getAuthHeaders() }
      );

      toast.success("Kassenbuchung wurde hinzugefügt");
      setShowAddDialog(false);
      resetForm();
      fetchSummary();
      fetchTransactions();
    } catch (error) {
      console.error("Error adding transaction:", error);
      toast.error("Fehler beim Hinzufügen der Kassenbuchung");
    }
  };

  const handleEditTransaction = async () => {
    try {
      if (!formData.amount || !formData.description || !formData.date) {
        toast.error("Bitte füllen Sie alle Pflichtfelder aus");
        return;
      }

      await axios.put(
        `${API_URL}/api/kasse/transactions/${selectedTransaction.id}`,
        {
          ...formData,
          amount: parseFloat(formData.amount),
          guest_id: formData.guest_id || null,
        },
        { headers: getAuthHeaders() }
      );

      toast.success("Kassenbuchung wurde aktualisiert");
      setShowEditDialog(false);
      resetForm();
      fetchSummary();
      fetchTransactions();
    } catch (error) {
      console.error("Error updating transaction:", error);
      toast.error("Fehler beim Aktualisieren der Kassenbuchung");
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/kasse/transactions/${selectedTransaction.id}`,
        { headers: getAuthHeaders() }
      );

      toast.success("Kassenbuchung wurde gelöscht");
      setShowDeleteDialog(false);
      setSelectedTransaction(null);
      fetchSummary();
      fetchTransactions();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("Fehler beim Löschen der Kassenbuchung");
    }
  };

  const handleViewReceipt = async (tx) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/kasse/transactions/${tx.id}/receipt`,
        { headers: getAuthHeaders() }
      );
      setReceiptData(response.data.receipt_data);
      setSelectedTransaction(tx);
      setShowReceiptDialog(true);
    } catch (error) {
      console.error("Error fetching receipt:", error);
      toast.error("Beleg konnte nicht geladen werden");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, receipt_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadReceipt = async (tx, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.post(
            `${API_URL}/api/kasse/transactions/${tx.id}/receipt`,
            { receipt_data: reader.result },
            { headers: getAuthHeaders() }
          );
          toast.success("Beleg wurde hochgeladen");
          fetchTransactions();
        } catch (error) {
          console.error("Error uploading receipt:", error);
          toast.error("Fehler beim Hochladen des Belegs");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const openEditDialog = (tx) => {
    setSelectedTransaction(tx);
    setFormData({
      transaction_type: tx.transaction_type,
      amount: tx.amount.toString(),
      description: tx.description,
      date: tx.date,
      category: tx.category || "",
      reference: tx.reference || "",
      guest_id: tx.guest_id || "",
      receipt_data: null,
    });
    setShowEditDialog(true);
  };

  // Scanner'ı aç (mevcut işlem için veya yeni işlem için)
  const openScanner = (tx = null) => {
    if (tx) {
      setScannerTransactionId(tx.id);
      setScannerDescription(tx.description);
    } else {
      setScannerTransactionId(null);
      setScannerDescription(formData.description || "");
    }
    setShowScannerDialog(true);
  };

  // Scanner tamamlandığında
  const handleScanComplete = (result) => {
    if (scannerTransactionId) {
      // Mevcut işleme eklendi
      fetchTransactions();
    } else {
      // Yeni işlem formuna ekle
      setFormData(prev => ({
        ...prev,
        receipt_data: result.pdf_base64
      }));
    }
    setShowScannerDialog(false);
  };

  const handlePreviewReport = async () => {
    setReportLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/kasse/report/pdf-preview`,
        { month: reportMonth, year: reportYear },
        { headers: getAuthHeaders() }
      );
      setReportPreview(`data:application/pdf;base64,${response.data.pdf_base64}`);
    } catch (error) {
      console.error("Error generating report preview:", error);
      toast.error("Fehler beim Erstellen der Vorschau");
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/kasse/report/pdf`,
        { month: reportMonth, year: reportYear },
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      
      const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", 
                          "Juli", "August", "September", "Oktober", "November", "Dezember"];
      const filename = `Kassenbericht_${monthNames[reportMonth - 1]}_${reportYear}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Kassenbericht wurde heruntergeladen");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Fehler beim Herunterladen des Berichts");
    } finally {
      setReportLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd.MM.yyyy", { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kasse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kassenführung</h1>
          <p className="text-slate-600 mt-1">Nakit gelir ve gider yönetimi</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setReportPreview(null);
              setShowReportDialog(true);
            }}
            data-testid="kassenbericht-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Kassenbericht
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setShowAddDialog(true);
            }}
            className="bg-[#c11718] hover:bg-[#a11415]"
            data-testid="add-cash-transaction-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Buchung
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Kassenbestand</p>
                <p className={`text-2xl font-bold mt-1 ${summary.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.current_balance)}
                </p>
              </div>
              <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center">
                <Wallet className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Einnahmen</p>
                <p className="text-2xl font-bold mt-1 text-green-600">
                  {formatCurrency(summary.total_income)}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Ausgaben</p>
                <p className="text-2xl font-bold mt-1 text-red-600">
                  {formatCurrency(summary.total_expense)}
                </p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Buchungen</p>
                <p className="text-2xl font-bold mt-1 text-slate-900">
                  {summary.transaction_count}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-xs text-slate-500">Von</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-slate-500">Bis</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-slate-500">Typ</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="income">Einnahmen</SelectItem>
                  <SelectItem value="expense">Ausgaben</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={fetchTransactions}>
                Filtern
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Kassenbuchungen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Bewohner</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead>Beleg</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Keine Buchungen gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => (
                    <TableRow key={tx.id} data-testid={`cash-tx-${tx.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(tx.date)}
                      </TableCell>
                      <TableCell>
                        {tx.transaction_type === "income" ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            <ArrowDownCircle className="h-3 w-3 mr-1" />
                            Einnahme
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            <ArrowUpCircle className="h-3 w-3 mr-1" />
                            Ausgabe
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {tx.description}
                      </TableCell>
                      <TableCell>
                        {tx.category && (
                          <Badge variant="outline">{tx.category}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tx.guest_name && (
                          <span className="text-sm text-slate-600">{tx.guest_name}</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${tx.transaction_type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.transaction_type === "income" ? "+" : "-"}
                        {formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell>
                        {tx.has_receipt ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewReceipt(tx)}
                            className="text-blue-600"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-[#c11718]"
                            onClick={() => openScanner(tx)}
                          >
                            <ScanLine className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openScanner(tx)}>
                              <ScanLine className="h-4 w-4 mr-2" />
                              Beleg scannen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(tx)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedTransaction(tx);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Kassenbuchung</DialogTitle>
            <DialogDescription>
              Erfassen Sie eine neue Einnahme oder Ausgabe
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Typ *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) => setFormData({ ...formData, transaction_type: value, category: "" })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">
                      <span className="flex items-center">
                        <ArrowDownCircle className="h-4 w-4 mr-2 text-green-600" />
                        Einnahme
                      </span>
                    </SelectItem>
                    <SelectItem value="expense">
                      <span className="flex items-center">
                        <ArrowUpCircle className="h-4 w-4 mr-2 text-red-600" />
                        Ausgabe
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Beschreibung *</Label>
              <Textarea
                placeholder="Beschreibung der Buchung..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Kategorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {(formData.transaction_type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.transaction_type === "income" && (
              <div>
                <Label>Bewohner (für Barmiete)</Label>
                <Select
                  value={formData.guest_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, guest_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Optional: Bewohner wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Bewohner</SelectItem>
                    {guests.map((guest) => (
                      <SelectItem key={guest.id} value={guest.id}>
                        {guest.first_name} {guest.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Referenz / Belegnummer</Label>
              <Input
                placeholder="z.B. Rechnungsnummer"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Beleg scannen</Label>
              <div className="mt-1">
                {formData.receipt_data ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">Beleg gescannt</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, receipt_data: null })}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-20 border-2 border-dashed hover:border-[#c11718] hover:bg-red-50"
                    onClick={() => {
                      setScannerDescription(formData.description);
                      setScannerTransactionId(null);
                      setShowScannerDialog(true);
                    }}
                  >
                    <div className="text-center">
                      <ScanLine className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-sm text-slate-500">Beleg scannen oder fotografieren</p>
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddTransaction} className="bg-[#c11718] hover:bg-[#a11415]">
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Buchung bearbeiten</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Typ *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) => setFormData({ ...formData, transaction_type: value, category: "" })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Einnahme</SelectItem>
                    <SelectItem value="expense">Ausgabe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Beschreibung *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Kategorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {(formData.transaction_type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Referenz</Label>
              <Input
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleEditTransaction} className="bg-[#c11718] hover:bg-[#a11415]">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Beleg anzeigen</DialogTitle>
            {selectedTransaction && (
              <DialogDescription>
                {selectedTransaction.description} - {formatCurrency(selectedTransaction.amount)}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="py-4">
            {receiptData && (
              <div className="flex flex-col items-center gap-4">
                {receiptData.startsWith("data:application/pdf") || receiptData.includes("pdf") ? (
                  <div className="w-full">
                    {/* PDF Preview using iframe with sandbox */}
                    <iframe
                      src={receiptData}
                      className="w-full h-[500px] border rounded bg-white"
                      title="Receipt PDF"
                      sandbox="allow-same-origin"
                    />
                    {/* Alternative: Direct link to open in new tab */}
                    <div className="mt-3 text-center">
                      <p className="text-sm text-slate-500 mb-2">
                        PDF-Vorschau funktioniert nicht? 
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const win = window.open();
                          win.document.write(`<iframe src="${receiptData}" style="width:100%;height:100%;border:none;"></iframe>`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        In neuem Tab öffnen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <img
                    src={receiptData}
                    alt="Beleg"
                    className="max-w-full max-h-[500px] object-contain rounded"
                  />
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            {receiptData && (
              <Button variant="outline" asChild>
                <a href={receiptData} download={`beleg_${selectedTransaction?.id}.${receiptData.includes('pdf') ? 'pdf' : 'jpg'}`}>
                  <Download className="h-4 w-4 mr-2" />
                  Herunterladen
                </a>
              </Button>
            )}
            <Button onClick={() => setShowReceiptDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buchung löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie diese Kassenbuchung löschen möchten?
              {selectedTransaction?.guest_id && (
                <span className="block mt-2 text-amber-600">
                  Hinweis: Diese Buchung ist mit einem Bewohnerkonto verknüpft. 
                  Die entsprechende Kontobuchung wird ebenfalls gelöscht.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDeleteTransaction}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kassenbericht Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Kassenbericht</DialogTitle>
            <DialogDescription>
              Wählen Sie Monat und Jahr für den Kassenbericht
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Monat</Label>
                <Select
                  value={reportMonth.toString()}
                  onValueChange={(v) => setReportMonth(parseInt(v))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Januar</SelectItem>
                    <SelectItem value="2">Februar</SelectItem>
                    <SelectItem value="3">März</SelectItem>
                    <SelectItem value="4">April</SelectItem>
                    <SelectItem value="5">Mai</SelectItem>
                    <SelectItem value="6">Juni</SelectItem>
                    <SelectItem value="7">Juli</SelectItem>
                    <SelectItem value="8">August</SelectItem>
                    <SelectItem value="9">September</SelectItem>
                    <SelectItem value="10">Oktober</SelectItem>
                    <SelectItem value="11">November</SelectItem>
                    <SelectItem value="12">Dezember</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>Jahr</Label>
                <Select
                  value={reportYear.toString()}
                  onValueChange={(v) => setReportYear(parseInt(v))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028].map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={handlePreviewReport}
                disabled={reportLoading}
              >
                {reportLoading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-slate-600 border-t-transparent rounded-full mr-2" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                Vorschau
              </Button>
            </div>

            {/* PDF Preview */}
            {reportPreview && (
              <div className="border rounded-lg overflow-hidden bg-slate-100">
                <iframe
                  src={reportPreview}
                  className="w-full h-[500px]"
                  title="Kassenbericht Preview"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Schließen
            </Button>
            <Button
              onClick={handleDownloadReport}
              disabled={reportLoading}
              className="bg-[#c11718] hover:bg-[#a11415]"
            >
              {reportLoading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              PDF Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Scanner Dialog */}
      <DocumentScanner
        isOpen={showScannerDialog}
        onClose={() => setShowScannerDialog(false)}
        onScanComplete={handleScanComplete}
        transactionId={scannerTransactionId}
        description={scannerDescription}
      />
    </div>
  );
}
